import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { PageLoadService } from '../services/page-load.service';
import { clearStoredSession, getStoredToken, setSessionEndedNotice } from './user-storage.util';

/**
 * Calls whose 401 is an answer about the request, not about the stored session,
 * so they never sign the browser out. Login and registration take credentials;
 * the four password-reset routes and the old send-temp-password alias are public
 * and must keep working for a browser that still holds an old session (connect
 * does not call the alias or refresh-token today; they are listed so a future
 * call cannot turn a refusal into a sign-out). The secrets fetch runs at startup
 * on every page, public ones included, sends whatever token is stored, and
 * handles its own failure; a refused session is caught by the page's own calls.
 */
const NO_SIGN_OUT_PATHS = [
  '/user/login',
  '/user/register',
  '/user/password-reset/request',
  '/user/password-reset/validate',
  '/user/password-reset/redeem',
  '/user/password-reset/resend',
  '/user/send-temp-password',
  '/auth/refresh-token',
  '/v1/ui-secrets',
];

/**
 * Ends the session of a browser whose session the server refuses (HTTP 401
 * after a password change elsewhere, a staff reset or a disabled account): it
 * clears the stored session, then loads the login page as a full page load, once.
 * The full load rebuilds every in-memory singleton (such as the cached project
 * list), so the next person to sign in on this tab sees nothing of the refused
 * session. The "signed out" toast would be lost with the page, so a one-shot
 * note in sessionStorage tells the login page to show it. Only the session that was refused is
 * signed out: a call sent with an older token than the stored one (for example a
 * poll in flight while Settings stored a fresh token after a password change)
 * leaves the fresh session alone. The failed request is never re-sent, and the
 * error still reaches the caller.
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const pageLoad = inject(PageLoadService);
  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        req.headers.has('Authorization') &&
        !isNoSignOutCall(req) &&
        refusedStoredSession(req)
      ) {
        // Cleared first, so the other calls refused alongside this one find no
        // session and the page is loaded only once.
        clearStoredSession();
        setSessionEndedNotice();
        pageLoad.load('/login');
      }
      return throwError(() => error);
    })
  );
};

function isNoSignOutCall(req: HttpRequest<unknown>): boolean {
  const path = new URL(req.url, window.location.origin).pathname;
  return NO_SIGN_OUT_PATHS.some((publicPath) => path.endsWith(publicPath));
}

/** True when a session is stored and the refused request carried that same bearer token. */
function refusedStoredSession(req: HttpRequest<unknown>): boolean {
  const stored = getStoredToken();
  const sent = /^Bearer\s+(\S+)\s*$/i.exec(req.headers.get('Authorization') ?? '')?.[1];
  return !!stored && sent === stored;
}
